/**
 * Detects DDL statements that affect routine or table metadata caches.
 *
 * Used by the query-execution pipeline to invalidate the
 * `routineParameterResolver` and `insertHintColumnResolver` caches after a
 * user runs a CREATE/ALTER/DROP statement.
 *
 * `isRoutineDdl` / `isTableDdl` / `isSchemaDdl` only scan the first 2 KB of the
 * SQL text — they classify the statement the caller is about to run, whose DDL
 * keyword sits at its very start (or immediately after a leading comment), so
 * scanning deeper is unnecessary and would slow down every query execution.
 *
 * `isDatabaseDdl` — "did the database LIST change?" — is the exception, and lives
 * in `databaseDdlDetector.ts`: it is fed the WHOLE script the user pressed Execute
 * on (`invalidateAfterDdl` in the query-execution pipeline), it is
 * engine-parameterised, and it classifies dynamic SQL. None of that is shared with
 * the prefix predicates here.
 */

import type { DatabaseEngineId } from '@/core/engines';

import { maskStringsAndComments, type MaskOptions } from './sqlTextMasker';

// The string/comment masker moved to `sqlTextMasker.ts` (its per-engine
// delimiter table outgrew this module); re-exported so the callers that import
// it from here — the navigational index's `ddlClassifier`, the IntelliSense
// slot detectors — keep working.
export {
  maskCommentsOnly,
  maskStringsAndComments,
  mysqlAnsiQuotesFromSqlMode,
  type MaskOptions,
} from './sqlTextMasker';

/** Scan limit — only inspect the first 2 KB. */
const SCAN_LIMIT = 2048;

/**
 * Matches CREATE/ALTER/DROP + optional OR REPLACE + FUNCTION/PROCEDURE/PACKAGE.
 *
 * Engine-specific patterns covered:
 *  - MSSQL:  CREATE FUNCTION, ALTER PROCEDURE, DROP FUNCTION
 *  - PG:     CREATE OR REPLACE FUNCTION
 *  - Oracle: CREATE OR REPLACE PACKAGE, CREATE OR REPLACE PACKAGE BODY
 *  - MySQL:  CREATE FUNCTION, DROP PROCEDURE
 *  - SQLite: N/A (no user-defined routines via SQL DDL)
 */
const ROUTINE_DDL_RE =
  /\b(CREATE|ALTER|DROP)\s+(OR\s+REPLACE\s+)?(FUNCTION|PROCEDURE|PACKAGE)\b/i;

/**
 * Matches CREATE/ALTER/DROP + optional OR REPLACE + TABLE/VIEW.
 *
 * Used by the insert-hint column resolver (table metadata cache).
 */
const TABLE_DDL_RE =
  /\b(CREATE|ALTER|DROP)\s+(OR\s+REPLACE\s+)?(TABLE|VIEW)\b/i;

/**
 * Returns `true` when the SQL text looks like it modifies routine metadata
 * (functions, procedures, packages).
 */
export function isRoutineDdl(sql: string): boolean {
  const head = sql.slice(0, SCAN_LIMIT);
  return ROUTINE_DDL_RE.test(head);
}

/**
 * Returns `true` when the SQL text looks like it modifies table/view
 * metadata (columns, constraints).
 */
export function isTableDdl(sql: string): boolean {
  const head = sql.slice(0, SCAN_LIMIT);
  return TABLE_DDL_RE.test(head);
}

/**
 * Matches the broader set of schema-shape DDL the IntelliSense schema bus
 * cares about. Used to invalidate `schemaBus` when the user runs DDL that
 * could change the schema snapshot's shape (tables, views, indexes, routines,
 * packages, sequences, synonyms, triggers, property graphs).
 *
 * Oracle `PACKAGE BODY` is captured by the optional `(\s+BODY)?` suffix.
 * `CREATE [TEMP|TEMPORARY] PROPERTY GRAPH` (PG): TEMP sits between verb and
 * object keyword, so it shares the modifier group with `OR REPLACE`.
 */
const SCHEMA_DDL_RE =
  /\b(CREATE|ALTER|DROP)\s+(OR\s+REPLACE\s+|TEMP(ORARY)?\s+)?(TABLE|VIEW|INDEX|FUNCTION|PROCEDURE|PACKAGE(\s+BODY)?|SEQUENCE|SYNONYM|TRIGGER|PROPERTY\s+GRAPH)\b/i;

/**
 * Returns `true` when the SQL text looks like schema-shape DDL — anything
 * that could alter the schema snapshot's tables / views / routines / indexes
 * / packages / sequences / synonyms / triggers.
 *
 * Strings and comments are masked before the regex runs so a value like
 * `'CREATE TABLE'` inside a SELECT cannot trigger a false positive. Pass
 * `engine` whenever the caller knows it — the masker's delimiter table is
 * engine-dependent (see `sqlTextMasker.ts`); omitting it falls back to that
 * module's unknown-engine row. `options` carries the rest of the masking
 * context the TEXT cannot disclose — the two MySQL `sql_mode` settings
 * (`ANSI_QUOTES`, which decides whether `DROP VIEW "v"` names a view or quotes
 * a string, and `NO_BACKSLASH_ESCAPES`, which decides whether `'it\'s'` is one
 * string or two).
 */
export function isSchemaDdl(
  sql: string,
  engine?: DatabaseEngineId,
  options?: Omit<MaskOptions, 'engine'>,
): boolean {
  const head = sql.slice(0, SCAN_LIMIT);
  const masked = maskStringsAndComments(head, { ...options, engine });
  return SCHEMA_DDL_RE.test(masked);
}
